import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core'
import { z } from 'zod'

const TaskBase = z
  .object({
    name: z.string().min(3).max(200),
    body: z.string().min(10).max(50000),
    teaser: z.string().min(10).max(200),
    ownerId: z
      .string()
      .min(1)
      .max(50)
      .regex(/^[a-zA-Z0-9_-]+$/)
      .optional(),
    language: z.enum(['SQL', 'Python']),
    difficulty: z.enum(['Easy', 'Intermediate', 'Hard', 'Expert']),
    description: z.string().min(20).max(10000),
    defaultQuery: z.string().min(1).max(2000),
    dataQuery: z.string().min(1).max(2000),
    seedFactor: z.number(),
    feedbackPayload: z.string().min(2).optional(),
    dataBank: z.string().min(2),
    accessGroups: z
      .array(
        z
          .string()
          .min(1)
          .max(50)
          .regex(/^[a-zA-Z0-9_-]+$/)
      )
      .min(1)
      .max(10),
    proficiencies: z
      .array(
        z.enum([
          'joins',
          'dataSanitization',
          'anomalyDetection',
          'timeSeriesAnalysis',
          'windowFunctions',
          'aggregation',
        ])
      )
      .optional(),
    solutionPlaceholder: z.string().optional(),
    availabilityWeight: z.number().optional(),
    deleted: z.boolean().optional(),
    sharedDBName: z.string().optional(),
    customQueryTimeout: z.number().gte(1).lte(255).optional(),
    slug: z.string().optional(),
  })
  .passthrough()
const Task = TaskBase.and(z.object({ id: z.string() }).passthrough())
const CountResponse = z.object({ count: z.number() }).passthrough()
const TaskErrorCode = z.enum(['taskNotFound', 'badRequest', 'invalidTaskId'])
const ErrorResponse = z
  .object({ error: z.string(), errorCode: TaskErrorCode.optional() })
  .passthrough()
const TaskUpdate = TaskBase

export const schemas = {
  TaskBase,
  Task,
  CountResponse,
  TaskErrorCode,
  ErrorResponse,
  TaskUpdate,
}

const endpoints = makeApi([
  {
    method: 'get',
    path: '/',
    alias: 'get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'accessGroups',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
      {
        name: 'ids',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
    ],
    response: z.array(Task),
  },
  {
    method: 'post',
    path: '/',
    alias: 'post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TaskBase,
      },
    ],
    response: Task,
  },
  {
    method: 'get',
    path: '/:_id',
    alias: 'get_id',
    requestFormat: 'json',
    parameters: [
      {
        name: '_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: Task,
    errors: [
      {
        status: 404,
        description: `Task not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'put',
    path: '/:_id',
    alias: 'put_id',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TaskBase,
      },
      {
        name: '_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: Task,
    errors: [
      {
        status: 404,
        description: `Task not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'delete',
    path: '/:_id',
    alias: 'delete_id',
    requestFormat: 'json',
    parameters: [
      {
        name: '_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Task not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/count',
    alias: 'getCount',
    requestFormat: 'json',
    response: z.object({ count: z.number() }).passthrough(),
  },
])

export const api = new Zodios(endpoints)

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options)
}
